import React from "react";
import { useDispatch, useSelector } from "react-redux";
import SpellPlannerGrid from "./SpellPlannerGrid";
import {
  Alliance,
  General,
  GovernyInfo,
  GovernySpells,
  MagicCore,
  Spell
} from "../../../../lib/types";
import { MAGIC_MODE } from "../../../../lib/enums";
import HelpIcon from "../../../Common/HelpIcon";
import { isSpellNegative, isSpellPositive } from "../../../../lib/commonFunctions";
import { setMagicMode, toggleMagicPlan } from "../../../../store/actions";
import { DragescentState } from "../../../../store/reducers/stateType";

interface IProps {
  govId?: number;
  mana: number;
  magicEnergy: number;
  castBlockMessage?: string | false;
  mageGeneral?: General;
  spells: GovernySpells;
  spellCastLimit: number;
  magicClosed: boolean;
  magicCore: MagicCore;
  magicMode: number;
  magicModeAftermath: number;
  readyToMagic: boolean;
  alliance: Alliance;
  war: any;
  opponent: Alliance;
}

const PlannerContent = ({
  govId,
  mana,
  magicEnergy,
  castBlockMessage,
  mageGeneral,
  spells,
  spellCastLimit,
  magicClosed,
  magicCore,
  magicMode,
  magicModeAftermath,
  readyToMagic,
  alliance,
  war,
  opponent
}: IProps) => {
  const dispatch = useDispatch();
  const id = useSelector((state: DragescentState) => state.governorate.id);

  return <>
    <h2 className="h2 h2-first">
      <span className="bordered">Spells planner</span>
    </h2>
    <span className="divider"/>
    {castBlockMessage
      ? <>
        <div className="box text-info">
          <p className={"negative"}>{castBlockMessage}</p>
        </div>
        <span className="divider"/>
      </>
      : <></>}
    <div className="box text-info">
      <p style={{ margin: "auto", paddingTop: "10px" }}>
        <i className="icon icon-kouzlo-poz middle"/> Buffs <i className="icon icon-kouzlo-poz middle"/>
      </p>
    </div>
    {mageGeneral && <SpellPlannerGrid
      govId={govId}
      mageGeneral={mageGeneral}
      availableSpells={spells.available
        .filter((spell: Spell) => spell.buff
          && (spell.generalSpell || (spells.learnt || []).includes(spell.id)))}
      castSpells={spells.cast || []}
      spellCastLimit={spellCastLimit}
      positive={true}
      closed={magicClosed}
      members={alliance ? alliance.members.filter((m: GovernyInfo) => m.id !== (govId || id)) : []}
    />}
    <span className="divider"/>
    <div className="box">
      <div className={"text-info"}>
        <p style={{ margin: "auto", paddingTop: "10px" }}>
          <i className="icon icon-kouzlo-neg middle"/> Debuffs and Damage <i className="icon icon-kouzlo-neg middle"/>
        </p>
      </div>
      {!war
        ? <div className={"text-info"}><p>Debuffs and Damage spells are not available out of war.</p></div>
        : <></>
      }
    </div>
    {mageGeneral && <SpellPlannerGrid
      govId={govId}
      mageGeneral={mageGeneral}
      availableSpells={spells.available
        .filter((spell: Spell) => !spell.buff
          && (spell.generalSpell || (spells.learnt || []).includes(spell.id)))
        .sort((s1: Spell, s2: Spell) => {
          if (s1.family === 5) {
            return -1;
          }
          if (s2.family === 5) {
            return 1;
          }
          if (s1.debuff) {
            return -1;
          }
          if (s2.debuff) {
            return 1;
          }

          return 0;
        })}
      castSpells={spells.cast || []}
      spellCastLimit={spellCastLimit}
      positive={false}
      closed={magicClosed}
      members={opponent ? opponent.members : []}
    />}
    <span className="divider"/>
    <div className={"box"}>
      <div className={"text-info"}>
        <p style={{ margin: "auto", paddingTop: "10px", paddingBottom: "20px" }}>
          <i className="icon icon-magie middle"/> Magic mode <i className="icon icon-magie middle"/>
        </p>
      </div>
      {!!magicModeAftermath && <p className={"negative"}>
        Magic modes need to cool down as it was activated last count.
      </p>}
      {magicClosed && <p>
        Magic modes can not be changed when magic is closed.
      </p>}
      <div className={"row"} style={{ paddingBottom: "15px" }}>
        <div className={`col-md-3${magicClosed || magicModeAftermath ? " text-disabled" : " grey"}`}>
          <div
            className={`radio-wrap${magicMode === MAGIC_MODE.NORMAL && !magicModeAftermath ? " checked" : ""}
              ${magicClosed || magicModeAftermath ? " disabled" : ""}`}
            onClick={() => (!magicClosed && !magicModeAftermath
              && magicMode !== MAGIC_MODE.NORMAL
              && dispatch(setMagicMode(MAGIC_MODE.NORMAL, govId, govId === id)))}
          >
            <input
              type={"radio"}
              checked={!magicModeAftermath && magicMode === MAGIC_MODE.NORMAL}
              disabled={!!magicModeAftermath || magicClosed}
            />
          </div>
          {!magicModeAftermath && magicMode === MAGIC_MODE.NORMAL ? <em>Normal</em> : "Normal"}
        </div>
        <div className={`col-md-3${magicClosed || magicModeAftermath ? " text-disabled" : " grey"}`}>
          <div
            className={`radio-wrap${magicMode === MAGIC_MODE.CORE_OVERLOAD
            || magicModeAftermath === MAGIC_MODE.CORE_OVERLOAD ? " checked" : ""}
              ${magicClosed || magicModeAftermath ? " disabled" : ""}`}
            onClick={() => (!magicClosed && !magicModeAftermath
              && magicMode !== MAGIC_MODE.CORE_OVERLOAD
              && dispatch(setMagicMode(MAGIC_MODE.CORE_OVERLOAD, govId, govId === id)))}
          >
            <input
              type={"radio"}
              checked={magicMode === MAGIC_MODE.CORE_OVERLOAD
              || magicModeAftermath === MAGIC_MODE.CORE_OVERLOAD}
              disabled={!!magicModeAftermath || magicClosed}
            />
          </div>
          {magicMode === MAGIC_MODE.CORE_OVERLOAD ? <em>Core overload</em> : "Core overload"}&nbsp;
          <HelpIcon
            title={"Core overload"}
            content={"All spell will be cast with +2 power this count. Next count, during cooldown, power of spells will decreased by -2."}
            iconClasses={["white"]}
          />
        </div>
        <div className={`col-md-3${magicClosed || magicModeAftermath ? " text-disabled" : " grey"}`}>
          <div
            className={`radio-wrap${magicMode === MAGIC_MODE.PROLONGED_DURATION
            || magicModeAftermath === MAGIC_MODE.PROLONGED_DURATION ? " checked" : ""}
              ${magicClosed || magicModeAftermath ? " disabled" : ""}`}
            onClick={() => (!magicClosed && !magicModeAftermath
              && magicMode !== MAGIC_MODE.PROLONGED_DURATION
              && dispatch(setMagicMode(MAGIC_MODE.PROLONGED_DURATION, govId, govId === id)))}
          >
            <input
              type={"radio"}
              checked={magicMode === MAGIC_MODE.PROLONGED_DURATION
              || magicModeAftermath === MAGIC_MODE.PROLONGED_DURATION}
              disabled={!!magicModeAftermath || magicClosed}
            />
          </div>
          {magicMode === MAGIC_MODE.PROLONGED_DURATION ? <em>Prolonged duration</em> : "Prolonged duration"}&nbsp;
          <HelpIcon
            title={"Prolonged duration"}
            content={"All spell with turns duration cast this count will last longer (+2 turns). Next count, during cooldown, duration will be shortened (-2 turns)."}
            iconClasses={["white"]}
          />
        </div>
        <div className={`col-md-3${magicClosed || magicModeAftermath ? " text-disabled" : " grey"}`}>
          <div
            className={`radio-wrap${magicMode === MAGIC_MODE.SPELL_RESISTANCE
            || magicModeAftermath === MAGIC_MODE.SPELL_RESISTANCE ? " checked" : ""}
              ${magicClosed || magicModeAftermath ? " disabled" : ""}`}
            onClick={() => (!magicClosed && !magicModeAftermath
              && magicMode !== MAGIC_MODE.SPELL_RESISTANCE
              && dispatch(setMagicMode(MAGIC_MODE.SPELL_RESISTANCE, govId, govId === id)))}
          >
            <input
              type={"radio"}
              checked={magicMode === MAGIC_MODE.SPELL_RESISTANCE
              || magicModeAftermath === MAGIC_MODE.SPELL_RESISTANCE}

              disabled={!!magicModeAftermath || magicClosed}
            />
          </div>
          {magicMode === MAGIC_MODE.SPELL_RESISTANCE ? <em>Spell resistance</em> : "Spell resistance"}&nbsp;
          <HelpIcon
            title={"Spell resistance"}
            content={"Your magic defence will gain +2 bonus for this count. Next count, during cooldown, magic defence will be decreased (-2)."}
            iconClasses={["white"]}
          />
        </div>
      </div>
    </div>
    <span className="divider"/>
    <div className="box inline">
      <div className="spacer">
        <p className="text-center">
          <strong>Total:</strong>&nbsp;
          You cast&nbsp;<span className={"negative"}>{(spells.cast || [])
            .filter((spell: any) => isSpellNegative(spell.id, spells.available)).length}
              </span>&nbsp;
          negative and&nbsp;
          <span className={"positive"}>{(spells.cast || [])
            .filter((spell: any) => isSpellPositive(spell.id, spells.available)).length}
              </span>&nbsp;
          positive spells for&nbsp;
          <em className="js-mana-used">{magicCore ? magicCore.mana : 0}</em> mana and <em className="js-mana-used">
          {magicCore ? magicCore.energy : 0}</em> energy.<br/>
          You can cast <em>{spellCastLimit}</em> spells.
          {!!magicMode && <><br />{magicMode === 1
            ? "Spells cast this count have +2 power"
            : magicMode === 2
              ? "Spell cast this count have +2 turns longer duration" : "Your magic defence is +2 during this count"}</>}
          {!!magicModeAftermath && <><br />{magicModeAftermath === 1
            ? "Magic mode cooldown: Spells cast this count have -2 power"
            : magicModeAftermath === 2
              ? "Magic mode cooldown: Spell cast this count have -2 turns longer duration"
              : "Magic mode cooldown: Your magic defence is -2 during this count"}</>}
          {!magicClosed && magicCore && (mana < magicCore.mana
            || magicEnergy < magicCore.energy)
          && <><br /><span className={"negative"}>Not enough mana or magic energy</span></>}
        </p>
      </div>
      <p className="text-center">
        <a href="#"
           className={`btn btn-blank btn-no-width ${!magicClosed
           && (!readyToMagic
             || !magicCore
             || mana < magicCore.mana
             || magicEnergy < magicCore.energy)
             ? "disabled" : ""}`}
           onClick={() => (!magicClosed
           && (!readyToMagic
             || !magicCore
             || mana < magicCore.mana
             || magicEnergy < magicCore.energy)
             ? null
             : dispatch(toggleMagicPlan(magicClosed
               ? 0 : 1, govId ? 1 : 0, govId, govId === id)))}
        >
          {magicClosed ? "Cancel cast" : "Cast spells"}
        </a>
      </p>
    </div>
    <span className={"divider"} />
  </>
}

export default PlannerContent;
